import { useCallback, useState } from 'react';
import { BrowserProvider, Contract, JsonRpcProvider, toUtf8Bytes } from 'ethers';
import { useWeb3 } from '../contexts/Web3Context';
import { useNetwork, type ChainInfo } from '../contexts/NetworkContext';
import { AGENT_TRUST_REGISTRY_ABI } from '../contracts/AgentTrustRegistryABI';

// Build-time fallbacks only — the runtime network selection (NetworkContext) wins.
const ENV_CONTRACT_ADDRESS = import.meta.env.VITE_AGENT_TRUST_REGISTRY_ADDRESS as string | undefined;
const ENV_CHAIN_ID = Number(import.meta.env.VITE_BLOCKCHAIN_CHAIN_ID ?? 99999);
const ENV_RPC_URL = import.meta.env.VITE_BLOCKCHAIN_RPC_URL as string | undefined;

const STATUS_NAMES = ['registered', 'active', 'suspended', 'decommissioned'] as const;

/** An agent's record in AgentTrustRegistry, as far as decommissioning needs it. */
export interface OnChainAgent {
  operator: string; // wallet allowed to act on it on-chain
  status: (typeof STATUS_NAMES)[number];
}

/**
 * Read an agent's on-chain record without needing a connected wallet.
 * Returns null when the agent was never registered on-chain (many agents are
 * created off-chain, through the API or SDK, and have nothing to decommission
 * there). Throws when the chain cannot be read, so a caller does not mistake
 * an RPC failure for "not on-chain".
 */
export async function readOnChainAgent(agentDid: string, chain?: ChainInfo | null): Promise<OnChainAgent | null> {
  const address = chain ? chain.contracts?.agentTrustRegistry : ENV_CONTRACT_ADDRESS;
  const rpcUrl = chain ? chain.rpcUrls?.[0] : ENV_RPC_URL;
  const chainId = chain?.chainId ?? ENV_CHAIN_ID;
  if (!address || !rpcUrl) return null;
  const contract = new Contract(address, AGENT_TRUST_REGISTRY_ABI, new JsonRpcProvider(rpcUrl, chainId));
  try {
    const r = await contract.getAgent(agentDid);
    return { operator: String(r.operator), status: STATUS_NAMES[Number(r.status)] ?? 'registered' };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    if (msg.includes('agent does not exist')) return null;
    throw e;
  }
}

export interface AgentTxResult {
  txHash: string;
  blockNumber: number;
}

export interface UseAgentTrustRegistryReturn {
  registerAgent: (agentDid: string, operatorDid: string, agentCard: object) => Promise<AgentTxResult>;
  decommissionAgent: (agentDid: string) => Promise<AgentTxResult>;
  /** Set an agent Active or Suspended on-chain. Only its operator wallet may. */
  setAgentStatus: (agentDid: string, status: 'active' | 'suspended') => Promise<AgentTxResult>;
  pending: boolean;
  contractAddress: string | undefined;
  isReady: boolean;
}

export function useAgentTrustRegistry(): UseAgentTrustRegistryReturn {
  const { signer, isConnected, isWrongNetwork, chainId } = useWeb3();
  const { selected } = useNetwork();
  const [pending, setPending] = useState(false);

  // Multi-chain: the selected network's registry address/chainId take priority.
  const CONTRACT_ADDRESS = selected?.contracts?.agentTrustRegistry || ENV_CONTRACT_ADDRESS;
  const CHAIN_ID = selected?.chainId ?? ENV_CHAIN_ID;

  const isReady =
    isConnected &&
    !isWrongNetwork &&
    !!signer &&
    !!CONTRACT_ADDRESS &&
    chainId === CHAIN_ID;

  const getContract = useCallback(() => {
    if (!signer || !CONTRACT_ADDRESS) {
      throw new Error('Wallet not connected or AgentTrustRegistry address not configured');
    }
    return new Contract(CONTRACT_ADDRESS, AGENT_TRUST_REGISTRY_ABI, signer);
  }, [signer, CONTRACT_ADDRESS]);

  const sendTx = useCallback(
    async (fn: () => Promise<{ hash: string; wait: () => Promise<{ blockNumber: number }> }>): Promise<AgentTxResult> => {
      setPending(true);
      try {
        const tx = await fn();
        const receipt = await tx.wait();
        return { txHash: tx.hash, blockNumber: receipt.blockNumber };
      } finally {
        setPending(false);
      }
    },
    [],
  );

  const registerAgent = useCallback(
    async (agentDid: string, operatorDid: string, agentCard: object): Promise<AgentTxResult> => {
      const contract = getContract();
      const agentCardBytes = toUtf8Bytes(JSON.stringify(agentCard));
      return sendTx(() => contract.registerAgent(agentDid, operatorDid, agentCardBytes));
    },
    [getContract, sendTx],
  );

  const decommissionAgent = useCallback(
    async (agentDid: string): Promise<AgentTxResult> => {
      const contract = getContract();
      return sendTx(() => contract.decommissionAgent(agentDid));
    },
    [getContract, sendTx],
  );

  const setAgentStatus = useCallback(
    async (agentDid: string, status: 'active' | 'suspended'): Promise<AgentTxResult> => {
      const contract = getContract();
      return sendTx(() => contract.updateAgentStatus(agentDid, STATUS_NAMES.indexOf(status)));
    },
    [getContract, sendTx],
  );

  return { registerAgent, decommissionAgent, setAgentStatus, pending, contractAddress: CONTRACT_ADDRESS, isReady };
}
